import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';
import Logo from '@/components/Logo';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/auth/forgot-password?email=${encodeURIComponent(email)}`);
      setSent(true);
      toast.success('Email inviata! Controlla la tua casella di posta.');
    } catch (error) {
      toast.error('Errore nell\'invio dell\'email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-4">
          <img 
            src="/zonepass-logo-final.png" 
            alt="ZonePass Logo" 
            className="h-40 md:h-48 lg:h-56 w-auto mx-auto mb-2"
          />
          <p className="text-gray-600 text-sm mb-3">Reset della password</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          {!sent ? (
            <>
              <h2 className="text-2xl font-semibold mb-2" data-testid="forgot-password-title">
                Password Dimenticata?
              </h2>
              <p className="text-gray-600 mb-6 text-sm">
                Inserisci il tuo indirizzo email e ti invieremo le istruzioni per reimpostare la password.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email-input"
                    className="mt-1"
                    placeholder="tua@email.com"
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="send-reset-link-button"
                >
                  {loading ? 'Invio...' : 'Invia Link di Recupero'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-8" data-testid="email-sent-confirmation">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Email Inviata!</h3>
              <p className="text-gray-600 mb-6">
                Controlla la tua casella di posta. Ti abbiamo inviato le istruzioni per reimpostare la password.
              </p>
              <p className="text-sm text-gray-500">
                Non hai ricevuto l'email? Controlla la cartella spam o{' '}
                <button
                  onClick={() => setSent(false)}
                  className="text-blue-600 hover:underline font-medium"
                >
                  riprova
                </button>
              </p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200">
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna al Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
